/* Fichiers — data-URL bornées et téléchargements authentifiés (spec §6.6).
 *
 * ⚠️ Rappel du piège porté par la spec : `GET /api/files/{path}`,
 * `GET /api/attachments/{id}/download` et
 * `GET /api/tracker/documents/{id}/download` sont authentifiés par Bearer.
 * En SPA, une balise `<img src="…">` ou un `<a download>` ne porte aucun
 * en-tête et reçoit un 401 : passer par `src/api/authFiles.ts` (images en
 * objectURL, téléchargements), qui s'appuie sur `fetchAuthenticatedBlob` +
 * `saveBlob` et donc sur l'intercepteur qui pose le jeton.
 * `isAuthenticatedApiUrl` reconnaît les trois formes ; l'extension aux
 * documents tracker (`^/api/tracker/documents/[\w-]+/download$`) est faite
 * (session 4).
 */

/** Taille maximale dépassée à la lecture d'un fichier. */
export class FileTooLargeError extends Error {
  readonly maxBytes: number;

  constructor(maxBytes: number) {
    super(`Le fichier dépasse la taille maximale de ${Math.round(maxBytes / 1024)} Ko.`);
    this.name = 'FileTooLargeError';
    this.maxBytes = maxBytes;
  }
}

/** Type MIME hors de l'allowlist passée à `fileToDataUrl`. */
export class UnsupportedFileTypeError extends Error {
  readonly mimeType: string;

  constructor(mimeType: string) {
    super('Ce type de fichier n\'est pas pris en charge.');
    this.name = 'UnsupportedFileTypeError';
    this.mimeType = mimeType;
  }
}

export interface DataUrlOptions {
  /** Taille maximale du fichier, en octets. */
  maxBytes: number;
  /** Préfixes MIME autorisés, ex. `['image/']`. Absent = tout type accepté. */
  accept?: string[];
  /** Préfixes MIME refusés même s'ils passent `accept`, ex. `['image/svg']`. */
  reject?: string[];
}

/**
 * `FileReader.readAsDataURL`, avec garde de taille ET de type.
 * Rejette avec `FileTooLargeError` ou `UnsupportedFileTypeError`.
 */
export function fileToDataUrl(file: File, options: DataUrlOptions): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (file.size > options.maxBytes) {
      reject(new FileTooLargeError(options.maxBytes));
      return;
    }
    const accepted = !options.accept || options.accept.some((prefix) => file.type.startsWith(prefix));
    const refused = options.reject?.some((prefix) => file.type.startsWith(prefix)) ?? false;
    if (!accepted || refused) {
      reject(new UnsupportedFileTypeError(file.type));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Lecture du fichier impossible.'));
      }
    };
    reader.onerror = () => reject(new Error('Lecture du fichier impossible.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Limites de taille des images encodées en data-URL dans un body JSON.
 * `clientLogo` passe de 375 Ko (Nuxt) à 360 Ko : à 375 Ko la data-URL dépasse
 * le `maxLength` 500 000 de `CreateClientBody.logo` et le serveur répond 400.
 */
export const DATA_URL_LIMITS = {
  clientLogo: 360 * 1024,
  linkIcon: 128 * 1024,
} as const;

/** Extrait le nom de fichier d'un en-tête `Content-Disposition`. */
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const encoded = /filename\*=\s*(?:UTF-8|utf-8)''([^;]+)/i.exec(header);
  if (encoded && encoded[1]) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      return encoded[1].trim();
    }
  }

  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted && quoted[1]) return quoted[1];

  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  if (bare && bare[1]) return bare[1].trim();

  return null;
}

/**
 * Télécharge via le SDK (Bearer posé par l'intercepteur) et rend le blob
 * accompagné du nom lu dans `Content-Disposition` (null s'il est absent).
 * ⚠️ `call` DOIT transmettre `parseAs: 'blob'` au client : sinon un corps
 * `text/*` ou JSON est consommé par le client et `response.blob()` échoue.
 */
export async function fetchAuthenticatedBlob(
  call: (options: { parseAs: 'blob' }) => Promise<{ data: unknown; response: Response }>,
): Promise<{ blob: Blob; filename: string | null }> {
  const { data, response } = await call({ parseAs: 'blob' });
  // Filet : un appelant qui ne transmet pas `parseAs` peut rendre autre chose qu'un Blob.
  const blob = data instanceof Blob ? data : await response.blob();
  return { blob, filename: filenameFromContentDisposition(response.headers.get('Content-Disposition')) };
}

/** Déclenche la sauvegarde navigateur d'un blob (objet URL révoqué derrière). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Révocation différée : Safari annule le téléchargement si l'URL meurt trop tôt.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
